import React, { useState } from "react";
import {
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Box,
  Typography,
} from "@mui/material";
import ModeCommentIcon from "@mui/icons-material/ModeComment";
import { SubmitFormAPI } from "../../Utility/UtilApi/UtilApi";
import UtilToastMessages from "../../Utility/UtilToastMessages/UtilToastMessages";

import { styled } from "@mui/system";

// Define Mild Color Palette
const colors = {
  primaryColor: "#8c7aa2", // Soft lavender-purple
  secondaryColor: "#f5f6f5", // Light grayish-white
  buttonColor: "#a3bffa", // Soft blue
  buttonHoverColor: "#7f9cf5", // Darker soft blue
  textColor: "#5a5a5a", // Mild gray
};

// Styled Chat Bubble with Animation
const AnimatedButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.primaryColor}, ${colors.buttonColor})`,
  width: "60px",
  height: "60px",
  borderRadius: "50%",
  minWidth: "unset",
  padding: "0",
  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    background: colors.buttonHoverColor,
    transform: "scale(1.1)",
  },
  animation: "float 3s ease-in-out infinite",
  "@keyframes float": {
    "0%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-10px)" },
    "100%": { transform: "translateY(0)" },
  },
}));

const Chatbot = () => {
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("enquiry");
  const [snackMessage, setSnackMessage] = useState("");
  const [severity, setSeverity] = useState("");
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "Mr",
    firstName: "",
    lastName: "",
    name: "",
    email: "",
    company: "",
    phone: "",
    description: "",
    subject: "",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => {
    setFormData({
      title: "Mr",
      firstName: "",
      lastName: "",
      name: "",
      email: "",
      company: "",
      phone: "",
      description: "",
      subject: "",
    });
    setErrors({});
    setIsOpen(false);
  };

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateName = (name) => {
    const nameRegex = /^[A-Za-z]+$/;
    return nameRegex.test(name);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\d+$/;
    return phoneRegex.test(phone);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    let error = "";

    if (name === "email" && value && !validateEmail(value)) {
      error = "Invalid email address";
    } else if (
      (name === "firstName" || name === "lastName") &&
      value &&
      !validateName(value)
    ) {
      error = "Name must contain only letters";
    } else if (name === "phone" && value && !validatePhone(value)) {
      error = "Phone number must contain only numbers";
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateFields = () => {
    const { firstName, lastName, email, phone } = formData;

    if (!firstName || !lastName || !email || !phone) {
      setSnackMessage("Please fill out all required fields (First Name, Last Name, Email, Phone).");
      setSnackbarOpen(true);
      setSeverity("error");
      return false;
    }

    if (!validateEmail(email)) {
      setSnackMessage("Please enter a valid email address.");
      setSnackbarOpen(true);
      setSeverity("error");
      return false;
    }

    if (!validatePhone(phone)) {
      setSnackMessage("Phone number must contain only numbers.");
      setSnackbarOpen(true);
      setSeverity("error");
      return false;
    }

    if (!validateName(firstName) || !validateName(lastName)) {
      setSnackMessage("First Name and Last Name must contain only letters.");
      setSnackbarOpen(true);
      setSeverity("error");
      return false;
    }

    return true;
  };

  const validateCaseFields = () => {
    const { email, description, subject } = formData;
    if (!validateEmail(email)) {
      setSnackMessage("Please enter a valid email address.");
      setSnackbarOpen(true);
      setSeverity("error");
      return false;
    }
    if (!subject) {
      setSnackMessage("Please provide a subject.");
      setSnackbarOpen(true);
      setSeverity("error");
      return false;
    }
    if (!description) {
      setSnackMessage("Please provide a description.");
      setSnackbarOpen(true);
      setSeverity("error");
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (selectedOption === "enquiry" && !validateFields()) {
      return;
    }
    if (selectedOption === "case" && !validateCaseFields()) {
      return;
    }

    try {
      const hasErrors = Object.values(errors).some((error) => error !== "");
      if (hasErrors) {
        setSnackMessage("Please correct the errors before submitting.");
        setSnackbarOpen(true);
        setSeverity("error");
        return;
      }

      let payload = [];
      if (selectedOption === "enquiry") {
        payload = {
          tablename: "leads",
          data: {
            name: `${formData.firstName} ${formData.lastName}`,
            firstname: formData.firstName,
            lastname: formData.lastName,
            title: formData.title,
            origin: "Website",
          },
        };
      } else if (selectedOption === "case") {
        payload = {
          tablename: "cases",
          data: {
            name: formData.email,
            stage: "In Progress",
            description: formData.description,
          },
        };
      }

      setLoading(true);
      const response = await SubmitFormAPI(payload);

      if (response.statusCode === 201) {
        setSnackMessage("Form submitted successfully");
        setSeverity("success");
        setSnackbarOpen(true);
      }
    } catch (exceptionalError) {
      setSnackMessage("An error occurred while submitting the form.");
      setSeverity("error");
      setSnackbarOpen(true);
      console.error("An error occurred:", exceptionalError);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  return (
    <>
      <UtilToastMessages
        isOpen={isSnackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        data={snackMessage}
        severity={severity}
      />
      <div style={styles.chatbotContainer}>
        {/* Chatbot Bubble */}
        <AnimatedButton onClick={handleOpen}>
          <ModeCommentIcon sx={{ fontSize: 28, color: "white" }} />
        </AnimatedButton>

        {/* Chatbot Popup */}
        <Dialog
          open={isOpen}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-container": {
              justifyContent: "flex-end",
              alignItems: "flex-end",
            },
            "& .MuiDialog-paper": {
              margin: "0 20px 50px 0",
              width: { xs: "90%", sm: "400px" },
              maxWidth: "400px",
              background: `linear-gradient(135deg, ${colors.secondaryColor} 0%, #e6e9f0 100%)`,
              borderRadius: "15px",
              boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
              position: "fixed",
              right: 0,
              bottom: 0,
            },
          }}
        >
          <DialogTitle
            sx={{
              background: colors.primaryColor,
              color: "white",
              fontWeight: "bold",
              textAlign: "center",
              padding: "15px",
              borderBottom: `2px solid ${colors.buttonColor}`,
            }}
          >
            <Typography variant="h6">Chat Support</Typography>
          </DialogTitle>
          <DialogContent sx={{ padding: "20px" }}>
            <Typography
              sx={{
                fontSize: "14px",
                color: colors.textColor,
                marginBottom: "20px",
                textAlign: "center",
                opacity: 0.9,
              }}
            >
              Sorry, all our chat agents are busy right now. Please choose an option:
            </Typography>

            <RadioGroup
              value={selectedOption}
              onChange={handleOptionChange}
              sx={{ marginBottom: "20px" }}
            >
              <FormControlLabel
                value="enquiry"
                control={
                  <Radio
                    sx={{
                      color: colors.primaryColor,
                      "&.Mui-checked": { color: colors.buttonColor },
                    }}
                  />
                }
                label="Submit an Enquiry"
                sx={{ color: colors.textColor }}
              />
              <FormControlLabel
                value="case"
                control={
                  <Radio
                    sx={{
                      color: colors.primaryColor,
                      "&.Mui-checked": { color: colors.buttonColor },
                    }}
                  />
                }
                label="Submit a Support Case"
                sx={{ color: colors.textColor }}
              />
            </RadioGroup>

            {/* Form Fields */}
            {selectedOption === "enquiry" && (
              <Box sx={styles.formContainer}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FormControl sx={{ width: "25%", marginTop: "10px" }} variant="outlined">
                    <InputLabel sx={{ color: colors.textColor }}>Title</InputLabel>
                    <Select
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      label="Title"
                      sx={{
                        color: colors.textColor,
                        backgroundColor: "rgba(255, 255, 255, 0.5)",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.primaryColor,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.buttonColor,
                        },
                        "& .MuiSvgIcon-root": { color: colors.textColor },
                      }}
                    >
                      <MenuItem value="Mr">Mr.</MenuItem>
                      <MenuItem value="Mrs">Mrs.</MenuItem>
                      <MenuItem value="Miss">Miss.</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="First Name"
                    fullWidth
                    margin="normal"
                    required
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    sx={styles.textField}
                  />
                </Box>
                <TextField
                  label="Last Name"
                  fullWidth
                  margin="normal"
                  required
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  sx={styles.textField}
                />
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  margin="normal"
                  required
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={styles.textField}
                />
                <TextField
                  label="Company"
                  fullWidth
                  margin="normal"
                  required
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  sx={styles.textField}
                />
                <TextField
                  label="Phone"
                  type="tel"
                  fullWidth
                  margin="normal"
                  required
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  sx={styles.textField}
                />
                <TextField
                  label="Description"
                  multiline
                  rows={4}
                  fullWidth
                  margin="normal"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  sx={styles.textField}
                />
              </Box>
            )}

            {selectedOption === "case" && (
              <Box sx={styles.formContainer}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  margin="normal"
                  required
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={styles.textField}
                />
                <TextField
                  label="Subject"
                  fullWidth
                  margin="normal"
                  required
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  sx={styles.textField}
                />
                <TextField
                  label="Description"
                  multiline
                  rows={4}
                  fullWidth
                  margin="normal"
                  required
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  sx={styles.textField}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: "15px 20px", backgroundColor: "rgba(0, 0, 0, 0.05)" }}>
            <Button
              onClick={handleClose}
              sx={{
                color: colors.textColor,
                "&:hover": { color: colors.buttonColor, backgroundColor: "rgba(163, 191, 250, 0.1)" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                background: `linear-gradient(90deg, ${colors.buttonColor}, ${colors.buttonHoverColor})`,
                color: "white",
                padding: "8px 20px",
                borderRadius: "20px",
                "&:hover": {
                  background: colors.buttonHoverColor,
                  transform: "scale(1.05)",
                },
                "&.Mui-disabled": {
                  background: "rgba(163, 191, 250, 0.5)",
                  color: "white",
                },
                transition: "all 0.3s ease",
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={16} color="inherit" sx={{ marginRight: "8px" }} />
                  Submitting
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

// Inline Styles
const styles = {
  chatbotContainer: {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    zIndex: 1000,
  },
  formContainer: {
    marginTop: "10px",
  },
  textField: {
    "& .MuiInputLabel-root": { color: colors.textColor },
    "& .MuiOutlinedInput-root": {
      color: colors.textColor,
      backgroundColor: "rgba(255, 255, 255, 0.5)",
      "& fieldset": { borderColor: colors.primaryColor },
      "&:hover fieldset": { borderColor: colors.buttonColor },
      "&.Mui-focused fieldset": { borderColor: colors.buttonColor },
    },
    "& .MuiFormHelperText-root": { color: "#e57373" },
  },
};

export default Chatbot;